import axios, { AxiosError } from "axios";
import { AccountAccessType, AccountLoginCheckResult, UserType } from "generated/CoreApi_v1";
import { SessionStore } from "services/SessionStore";
import { SessionService } from "services/sessionService";

export type AuthResult = {
    jwt: Nullable<AuthJwt>;
    loginStatus: AccountLoginCheckResult | undefined;
    errorMessage: Nullable<string>;
    continueAuthStamp: Nullable<string>;
};

export type AuthUser = AppUser & {
    locale: string;
    keepConnected: boolean;
    signedAggrement: boolean;
    continueAuthStamp?: string;
    lastUpdate: number;
    accessType: AccountAccessType | undefined;
    userType: UserType;
    permissions: string[];
    spyUserId: string | null;
    spyUsername: string | null;
};

export type AuthRefreshUserProps = {
    name: string;
    locale: string;
    email?: string | null;
    thumbnailUrl?: string;
};

export type AuthJwtDecoded = {
    name: string;
    email: string;
    locale: string;
    picture: string;
    zoneinfo: string;
    continue_auth_stamp: string;
    keep_connected: string;
    signed_aggrement: string;
    access_type: string;
    userType: string;
    loginStatus: string;
    permissions: string;
    spyUserId: string | null;
    spyUsername: string | null;

    // OpenId
    client_id: string;
    sub: string;
    kid: string;
    aud: string;
    iss: string;
    exp: number;
    iat: number;
};

export type AuthJwt = {
    access_token: string;
    token_type: string;
    expires_in: number;
    refresh_token: string;
    permissions: string;
    continueAuthStamp: string | null;
    thumbnailUrl: string;
};

type AuthPasswordParams = {
    authValue: string;
    password: string;
};

//type AuthCheckParams = {
//    authField: string;
//    authValue: string;
//};

type AuthProblemDetail = {
    error: string;
    error_description: string;
    error_uri: string;
    scope: string;
};

export class TokenRenew {
    private _updating: boolean;
    private _newToken: AuthJwt | null;

    constructor() {
        this._updating = false;
        this._newToken = null;
    }

    public get isUpdating() {
        return this._updating;
    }

    public get newToken() {
        return this._newToken;
    }

    public startUpdate() {
        this._updating = true;
        this._newToken = null;
    }

    public endUpdate(token: AuthJwt | null) {
        this._newToken = token;
        this._updating = false;
    }
}

export const AnonymousUser: AuthUser = {
    accessType: undefined,
    userType: UserType.Internal,
    locale: "pt-BR",
    email: "",
    keepConnected: false,
    lastUpdate: 0,
    name: "",
    signedAggrement: false,
    thumbnailUrl: "",
    userId: "",
    permissions: [],
    spyUserId: null,
    spyUsername: null
};

export class AuthService {

    static get accessChecked() {
        return window.accessChecked ?? false;
    }

    static set accessChecked(value: boolean) {
        window.accessChecked = value;
    }

    static passwordSignIn(p: AuthPasswordParams): Promise<AuthResult> {
        var bodyFormData = new URLSearchParams();
        bodyFormData.append('username', p.authValue.trim());
        bodyFormData.append('password', p.password.trim());
        bodyFormData.append('scope', "offline_access");
        bodyFormData.append('client_id', "powerminds");
        bodyFormData.append('grant_type', "password");
        bodyFormData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

        return axios.post<AuthJwt>(SessionService.current.apiPaths.openid + "/connect/token", bodyFormData, { withCredentials: true })
            .then((res) => {
                this.accessChecked = true;
                const jwt = res.data;
                return {
                    loginStatus: AccountLoginCheckResult.Valid,
                    errorMessage: null,
                    continueAuthStamp: null,
                    jwt
                };
            })
            .catch((error: AxiosError<AuthProblemDetail> | Error) => {

                if (axios.isAxiosError(error)) {
                    const e = error.response!.data;
                    return {
                        loginStatus: (AccountLoginCheckResult as any)[e.error],
                        errorMessage: e.error_description,
                        continueAuthStamp: e.scope,
                        jwt: null
                    };
                }

                return {
                    loginStatus: undefined,
                    errorMessage: error.message,
                    continueAuthStamp: null,
                    jwt: null
                };
            });
    }

    static samlSignIn(samlResponse: string): Promise<AuthResult> {
        var bodyFormData = new URLSearchParams();
        bodyFormData.append('username', samlResponse);
        bodyFormData.append('password', "839F29F8F9704C1997D59522543408C5");
        bodyFormData.append('scope', "offline_access");
        bodyFormData.append('client_id', "powerminds");
        bodyFormData.append('grant_type', "password");
        bodyFormData.append('grant_subtype', "saml");
        bodyFormData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

        return axios.post<AuthJwt>(SessionService.current.apiPaths.openid + "/connect/token", bodyFormData, { withCredentials: true })
            .then((res) => {
                this.accessChecked = true;
                const jwt = res.data;
                return {
                    loginStatus: AccountLoginCheckResult.Valid,
                    errorMessage: null,
                    continueAuthStamp: null,
                    jwt
                };
            })
            .catch((error: AxiosError<AuthProblemDetail> | Error) => {

                if (axios.isAxiosError(error)) {
                    const e = error.response!.data;
                    return {
                        loginStatus: (AccountLoginCheckResult as any)[e.error],
                        errorMessage: e.error_description,
                        continueAuthStamp: e.scope,
                        jwt: null
                    };
                }

                return {
                    loginStatus: undefined,
                    errorMessage: error.message,
                    continueAuthStamp: null,
                    jwt: null
                };
            });
    }

    static spySignIn(authorizationId: string, password: string): Promise<AuthResult> {
        var bodyFormData = new URLSearchParams();
        bodyFormData.append('username', authorizationId.trim());
        bodyFormData.append('password', password.trim());
        bodyFormData.append('scope', "offline_access");
        bodyFormData.append('client_id', "powerminds");
        bodyFormData.append('grant_type', "password");
        bodyFormData.append('grant_subtype', "spy");
        bodyFormData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

        return axios.post<AuthJwt>(SessionService.current.apiPaths.openid + "/connect/token", bodyFormData, { withCredentials: true })
            .then((res) => {
                this.accessChecked = true;
                const jwt = res.data;
                return {
                    loginStatus: AccountLoginCheckResult.Valid,
                    errorMessage: null,
                    continueAuthStamp: null,
                    jwt
                };
            })
            .catch((error: AxiosError<AuthProblemDetail> | Error) => {

                if (axios.isAxiosError(error)) {
                    const e = error.response!.data;
                    return {
                        loginStatus: (AccountLoginCheckResult as any)[e.error],
                        errorMessage: e.error_description,
                        continueAuthStamp: e.scope,
                        jwt: null
                    };
                }

                return {
                    loginStatus: undefined,
                    errorMessage: error.message,
                    continueAuthStamp: null,
                    jwt: null
                };
            });
    }

    static openSamlDialog(url: string) {
        var w = 600;
        var h = 450;
        var left = (window.screen.width / 2) - (w / 2);
        var top = (window.screen.height / 2) - (h / 2);

        window.open(url, "PowerMinds External Authentication",
            `
            width=${w},
            height=${h},
            top=${top},
            left=${left},
            scrollbars=0,
            toolbar=0,
            location=0,
            menubar=0
            `
        );
    }

    static accountLogout() {
        return new Promise<void>((resolve) => {
            var bearer = SessionStore.bearerString();
            if (bearer != "") {
                const headers: { [key: string]: string; } = {};
                headers["Accept"] = "application/json";
                headers["Authorization"] = bearer;
                axios.post(SessionService.current.apiPaths.coreApi + "/AccountLogout", {}, { headers })
                    .catch((e) => console.error(e))
                    .finally(() => resolve());
            }
            else resolve();
        });
    }

    static IsUserYetAuthenticated() {
        return new Promise<boolean>((resolve) => {
            var bearer = SessionStore.bearerString();
            if (bearer != "") {
                const headers: { [key: string]: string; } = {};
                headers["Accept"] = "application/json";
                headers["Authorization"] = bearer;
                axios.post(SessionService.current.apiPaths.coreApi + "/CheckAccess", {}, { headers })
                    .catch((e) => console.error(e))
                    .finally(() => {
                        this.accessChecked = true;
                        resolve(true);
                    });
            }
            else {
                this.accessChecked = true;
                resolve(false);
            }
        });
    }

    static logout() {
        this.accessChecked = false;
        return axios.get(SessionService.current.apiPaths.openid + "/connect/logout", { withCredentials: true });
    }

    /**
     *  Renova o token de autenticação.
    */
    static updateToken(tokenRenewInstance: TokenRenew): Promise<Nullable<AuthJwt>> {
        if (!tokenRenewInstance.isUpdating) {

            const refreshToken = SessionStore.readToken()?.refresh_token;

            tokenRenewInstance.startUpdate();

            return new Promise<Nullable<AuthJwt>>((resolve) => {

                if (refreshToken) {
                    var bodyFormData = new URLSearchParams();
                    bodyFormData.append('client_id', "powerminds");
                    bodyFormData.append('grant_type', "refresh_token");
                    bodyFormData.append('refresh_token', refreshToken);

                    const url = SessionService.current.apiPaths.coreApi + "/connect/token";
                    return axios.post<AuthJwt>(url, bodyFormData, { withCredentials: true })
                        .then((res) => {
                            if (res && res.data) {
                                const jwt = res.data;
                                SessionStore.storeJWT(jwt, true);
                                tokenRenewInstance.endUpdate(jwt);
                                resolve(jwt);
                            }
                            else {
                                console.warn('Refresh token is no more valid.');
                                tokenRenewInstance.endUpdate(null);
                                resolve(null);
                            }
                        })
                        .catch((error) => {
                            console.error(`Error while refreshing token.\nAPI URL: ${url}\nError: ${error}`);
                            tokenRenewInstance.endUpdate(null);
                            resolve(null);
                        });
                }
                else {
                    tokenRenewInstance.endUpdate(null);
                    resolve(null);
                }
            });
        }

        // Aguarda o primeiro request de novo login terminar.
        return new Promise<Nullable<AuthJwt>>((resolve) => {
            var id = setInterval(() => {
                if (!tokenRenewInstance.isUpdating) {
                    clearInterval(id);
                    resolve(tokenRenewInstance.newToken);
                }
            }, 1000);
        });
    }
}
